import { ArrowLeftOutlined } from '@ant-design/icons';
import { EnumCambiamentoStatoRequestTransizione } from '@framework/api-client/api/inquiries-compile/schemas/enumCambiamentoStatoRequestTransizione.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { SubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/SubmissionModuleState.ts';
import {
  type IModuleStateMachineRecord,
  ModuleStateMachine,
} from '@framework/frontoffice-submission/module/fsm/ModuleStateMachine.ts';
import { ActionButtonContainer } from '@framework/frontoffice-submission/submission/signer/ActionButtonContainer.tsx';
import { ActionMessage } from '@framework/frontoffice-submission/submission/signer/ActionMessage.tsx';
import { AsyncButton } from '@framework/ui/components/button/AsyncButton.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubmissionModuleStateChanger } from './useSubmissionModuleStateChanger.ts';

export interface IBackToCompilationButtonProps {
  data: IModuleStateMachineRecord;
}

export function BackToCompilationButton(props: IBackToCompilationButtonProps) {
  const { data } = props;

  const currentUser = useCurrentUser();
  const { t } = useTranslation();

  const canReturnToCompilation = useMemo(() => {
    return ModuleStateMachine.canTransition(data, {
      from: SubmissionModuleState.Completed,
      to: SubmissionModuleState.Compilation,
      user: currentUser,
    });
  }, [currentUser, data]);

  const { handleClick } = useSubmissionModuleStateChanger({
    data,
    successMessage: t(
      'submission-action-button.modulo-nuovamente-compilabile',
      'Modulo nuovamente compilabile',
    ),
  });

  const isSignable = useMemo(() => {
    return ModuleStateMachine.can(data, '.signable', currentUser).allowed;
  }, [currentUser, data]);

  const statusMessage = isSignable
    ? t(
        'submission-action-button.modulo-compilato-e-firmato-precedentemente',
        'Il modulo è già stato compilato e firmato.',
      )
    : t(
        'submission-action-button.modulo-compilato-precedentemente',
        'Il modulo è già stato compilato.',
      );

  if (
    !canReturnToCompilation.allowed &&
    canReturnToCompilation.message === 'is-not-manager'
  ) {
    return <ActionMessage message={statusMessage} type="info" />;
  }

  if (!canReturnToCompilation.allowed) return null;

  return (
    <ActionButtonContainer
      message={
        <ActionMessage
          message={
            isSignable
              ? t(
                  'submission-action-button.modulo-compilato-e-firmato',
                  'Il modulo è stato compilato e firmato. Riportando il modulo in compilazione, sarà necessario firmarlo nuovamente.',
                )
              : t(
                  'submission-action-button.modulo-compilato',
                  'Il modulo è stato compilato.',
                )
          }
          type="info"
        />
      }
      button={
        <AsyncButton
          block
          size="large"
          onClick={() =>
            handleClick(
              EnumCambiamentoStatoRequestTransizione.RIPORTA_IN_COMPILAZIONE,
            )
          }
        >
          <ArrowLeftOutlined aria-hidden="true" />{' '}
          {t(
            'submission-action-button.ritorna-in-compilazione',
            'Ritorna in compilazione',
          )}
        </AsyncButton>
      }
    />
  );
}
